/**
 * Iter60.p16 — Shared "Map biometric USERID → employee" dialog.
 *
 * Extracted from `AttendanceExceptions.jsx` so both the Exception Queue
 * and the main Attendance page's Events section can offer the same
 * link affordance for `pending_review` events with
 * `exception_reason = unmapped_biometric_user_id`.
 *
 * Contract:
 *   props:
 *     state:       { ev, dev } | null  — the unmapped event + its device meta
 *     onClose:     () => void
 *     onMapped:    () => void         — parent reloads after success
 *     items:       AttendanceEvent[]  — used to compute the "N other pending" peer count
 *     employees:   Employee[]         — active-employee dictionary values
 *
 * The dialog posts to `POST /api/attendance/events/{id}/map-biometric`,
 * which bulk-updates ALL past unmapped events with the same
 * `(device_id, raw_biometric_user_id)` and re-runs `recompute_daily`
 * for each touched work_date. See `devices.py:map_unmapped_event`.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { formatDateTime } from "../utils/date";

export default function MapBiometricDialog({ state, onClose, onMapped, items, employees }) {
    const [q, setQ] = useState("");
    const [pickedId, setPickedId] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { setQ(""); setPickedId(""); }, [state?.ev?.id]);

    const peerCount = useMemo(() => {
        if (!state?.ev) return 0;
        const raw = state.ev.raw_biometric_user_id;
        const dev = state.ev.device_id;
        return (items || []).filter((e) =>
            e.exception_reason === "unmapped_biometric_user_id"
            && e.raw_biometric_user_id === raw
            && e.device_id === dev
        ).length;
    }, [items, state]);

    const filtered = useMemo(() => {
        if (!state?.ev) return [];
        const lower = q.toLowerCase();
        const moduleId = state.dev?.module_id;
        const active = (employees || []).filter((e) => e.status === "active");
        const ranked = active.map((e) => ({
            e,
            score: (e.module_id === moduleId ? 0 : 100)
                 + ((e.full_name || "").toLowerCase().includes(lower) || (e.employee_code || "").toLowerCase().includes(lower) ? 0 : 1000),
            visible: !q || (e.full_name || "").toLowerCase().includes(lower) || (e.employee_code || "").toLowerCase().includes(lower),
        })).filter((x) => x.visible).sort((a, b) => a.score - b.score).map((x) => x.e);
        return ranked.slice(0, 25);
    }, [q, state, employees]);

    const submit = async () => {
        if (!pickedId) { toast.error("Pick an employee"); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/attendance/events/${state.ev.id}/map-biometric`, {
                employee_id: pickedId,
            });
            const emp = (employees || []).find((e) => e.id === pickedId);
            const otherCount = Math.max(0, peerCount - 1);
            toast.success(
                `Mapped UserID ${data.biometric_user_id} on ${state.dev?.serial_number || "device"} → ${emp?.full_name || "employee"}.${otherCount > 0 ? ` ${otherCount} other event${otherCount === 1 ? "" : "s"} also resolved.` : ""}`
            );
            onMapped();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    if (!state) return null;
    const ev = state.ev;
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="exc-map-dialog" className="max-w-lg">
                <DialogHeader><DialogTitle>Map biometric USERID to employee</DialogTitle></DialogHeader>
                <div className="space-y-3 text-sm">
                    <div className="p-3 rounded-md bg-[color:var(--asl-bg,#F5F6F8)] grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        <div><span className="text-[color:var(--asl-muted)]">Device:</span> <span className="asl-navy-text font-medium">{state.dev?.device_name || "—"}</span></div>
                        <div><span className="text-[color:var(--asl-muted)]">SN:</span> <span className="mono">{state.dev?.serial_number || "—"}</span></div>
                        <div><span className="text-[color:var(--asl-muted)]">Raw USERID:</span> <span className="mono asl-crimson-text">#{ev.raw_biometric_user_id}</span></div>
                        <div><span className="text-[color:var(--asl-muted)]">When:</span> <span className="mono">{formatDateTime(ev.event_time)}</span></div>
                        <div><span className="text-[color:var(--asl-muted)]">Verify:</span> <span>{ev.verification_method || "—"}</span></div>
                        <div><span className="text-[color:var(--asl-muted)]">Other pending:</span> <span className="font-semibold asl-navy-text">{Math.max(0, peerCount - 1)}</span></div>
                    </div>

                    <div>
                        <Input placeholder="Search active employees by name or code…" value={q}
                               onChange={(e) => setQ(e.target.value)} data-testid="exc-map-search" />
                        <div className="mt-2 max-h-64 overflow-y-auto border rounded-md divide-y" style={{ borderColor: "var(--asl-border)" }}>
                            {filtered.length === 0 ? (
                                <div className="p-3 text-xs text-[color:var(--asl-muted)] italic">No matches.</div>
                            ) : filtered.map((e) => (
                                <label key={e.id}
                                       className={`flex items-center gap-2 p-2 cursor-pointer text-xs ${pickedId === e.id ? "bg-[color:var(--asl-bg,#F5F6F8)]" : ""}`}
                                       data-testid={`exc-map-emp-${e.id}`}>
                                    <input type="radio" name="emp-picker" checked={pickedId === e.id}
                                           onChange={() => setPickedId(e.id)} />
                                    <div className="flex-1">
                                        <div className="font-medium asl-navy-text">{e.full_name}</div>
                                        <div className="mono text-[10px] asl-crimson-text">{e.employee_code} · {e.job_title || "—"}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                    <p className="text-[10px] text-[color:var(--asl-muted)]">
                        Mapping also enrolls the USERID on the device for future events. Past unmapped events from the same USERID + device will flip to accepted and their daily summaries will be recomputed.
                    </p>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} className="asl-navy-bg text-white" disabled={busy || !pickedId}
                            data-testid="exc-map-submit">Map &amp; accept</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
